import classnames from "classnames";
import { memo, useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import { useCloseOthersOnOpen } from "src/hooks/useCloseOthersOnOpen";
import usePopoverClose from "../../../../hooks/usePopoverClose";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { WRITE_MODE_AUTO, WRITE_MODE_GUIDED, WRITE_MODE_TONE } from "../../../../../../src/shared/constants.js";
import { CrossAppSignal, useCrossAppSignalStore } from "../../../../stores/crossAppSignalStore";
import { useStickyTourStore } from "../../../../stores/stickyTourStore.js";
import { makeId, sendAppEvent } from "../../../../Utils.js";
import AutoPlaySilentVideo from "../../../AutoPlaySilentVideo";
import TryUnlimitedAndTryMuseTooltip from "../../../MultiUseTooltip/TryUnlimitedAndTryMuseTooltip/index.tsx";
import StickyNoteAnchor from "../../../StickyTour/StickyNoteAnchor.js";
import { AnchorIds, TourSignal } from "../../../StickyTour/TourTypes";
import WriteMenu from "../../../WriteMenu/index.js";

const WriteButton = memo(function WriteButton({
  disabled,
  showTryUnlimitedTooltip,
  showTryMuseTooltip,
  onTryUnlimitedClosed,
  onTryMuseClosed,
  defaultTooltipDisabled,
  wormholeButtonRef,
  writeOpen,
  setWriteOpen,
  guidanceOpen,
  writeMode,
  pluginOpenWormhole,
  openGuidance,
  hasMemory,
  tone,
  memory,
  setMemory,
  memoryEnabled,
  setMemoryEnabled,
  tones,
  editorOptions,
  wormholeOutputLength,
  wormholeOutputCards,
  creativitySliderPosition,
  setEditorOptions,
  setTone,
  openWormhole,
  writeModel,
  modalOpen,
  setModalOpen,
  setWriteMode,
  myVoiceDisplayName,
  showMyVoiceOnboarding,
  onMyVoiceOnboardingClosed,
  metadata,
  setMetadata,
}) {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const isWriteRunning = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.WRITE_RUNNING]);
  const containerClass = classnames("wormhole", {
    disabled: disabled || isWriteRunning,
    "toolbar-dropdown-open": writeOpen,
  });
  const isMyVoiceWriteModel = editorOptions?.writeModel === "myVoice";
  const effectiveVoiceName = myVoiceDisplayName || "Your Import";
  const defaultTooltipContent = isMyVoiceWriteModel ? (
    <>
      <strong>Voice: {effectiveVoiceName}</strong>
    </>
  ) : (
    <>
      <strong>Place your cursor at the end of some text and click Write</strong>. Sudowrite will continue it! (The more text in your doc, the better.)
    </>
  );

  const baseModeLabel = (() => {
    if (writeMode === WRITE_MODE_AUTO) return "Auto";
    if (writeMode === WRITE_MODE_GUIDED) return "Guided";
    if (writeMode === WRITE_MODE_TONE) return tone?.label || "Tone";
    return writeMode?.label || "Write";
  })();

  const writeStateCopy = isMyVoiceWriteModel ? (writeMode === WRITE_MODE_AUTO ? "My Voice" : `${baseModeLabel} • My Voice`) : baseModeLabel;
  const showKeyDetails = writeMode === WRITE_MODE_AUTO && hasMemory;
  const { announceOpen } = useCloseOthersOnOpen("sudowrite:toolbar-dropdown-open", () => setWriteOpen(false), ["sudokit:select-open"]);
  const chevronRef = useRef(null);

  const { containerRef, handleBlur } = usePopoverClose(writeOpen, () => setWriteOpen(false), {
    clickOutside: false,
    paused: modalOpen,
    onEscapeFocus: () => chevronRef.current?.focus(),
  });

  // Focus first menu item when dropdown opens
  useEffect(() => {
    if (writeOpen && containerRef.current) {
      requestAnimationFrame(() => {
        const firstItem = containerRef.current.querySelector('[role="menuitem"]');
        if (firstItem) firstItem.focus();
      });
    }
  }, [writeOpen]);

  return (
    <li
      ref={containerRef}
      className={containerClass}
      onBlur={handleBlur}
    >
      <TryUnlimitedAndTryMuseTooltip
        showTryUnlimitedTooltip={showTryUnlimitedTooltip}
        showTryMuseTooltip={showTryMuseTooltip}
        onTryUnlimitedClosed={onTryUnlimitedClosed}
        onTryMuseClosed={onTryMuseClosed}
        defaultTooltipContent={defaultTooltipContent}
        defaultTooltipDisabled={defaultTooltipDisabled}
        tryUnlimitedImage="/assets/pick-unlimited-prose-mode.gif"
        tryMuseImage="/assets/pick-muse-prose-mode.gif"
        tryUnlimitedArrowOffset={45}
        tryMuseArrowOffset={45}
        showMyVoiceTooltip={isMyVoiceWriteModel && !!showMyVoiceOnboarding}
        onMyVoiceClosed={onMyVoiceOnboardingClosed}
        myVoiceArrowOffset={-5}
        myVoiceContent={
          isMyVoiceWriteModel ? (
            <div className="w-[360px] p-2 max-w-[90vw]">
              <div className="mb-2 text-left w-80">
                <p className="text-base mb-2 font-medium mb-1 leading-[1.5]">
                  We trained <strong>My Voice</strong> on <em>{effectiveVoiceName}</em>.
                </p>
                <p className="text-base font-medium mb-1 leading-[1.5]">
                  Now <strong>Write</strong> will sound just like you!
                </p>
              </div>
              <div className="relative">
                <AutoPlaySilentVideo
                  immediate
                  className="w-[calc(100%-20px)] ml-[-16px] mt-1 h-auto rounded border-[1px] border-[rgba(200,200,200,0.5)]"
                  mp4URL="/assets/my-voice-tooltip.mp4"
                />
                <div className="absolute bottom-4 right-7">
                  <SudoButton
                    variant="tertiaryDarkForOverlays"
                    size="default"
                    as="a"
                    href="https://sudowrite.notion.site/My-Voice-Beta-Participant-Notes-2247975fa0ec805fb4b5d19c0a936589?pvs=74"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Learn more about My Voice"
                  >
                    Learn More
                  </SudoButton>
                </div>
              </div>
            </div>
          ) : null
        }
      >
        <div className={classnames({ "wormhole-item": true, "my-voice-active": isMyVoiceWriteModel })}>
          <StickyNoteAnchor anchorId={AnchorIds.WRITE_BUTTON} />
          <button
            type="button"
            className={classnames("open-wormhole use-component-styles", { "my-voice-active": isMyVoiceWriteModel })}
            ref={wormholeButtonRef}
            onClick={() => {
              if (isWriteRunning) return;

              sendFlowSignal(TourSignal.WRITE_BUTTON_CLICKED);
              if (writeMode === WRITE_MODE_TONE) {
                sendAppEvent("Write - Tone Shift Used", {
                  tone: tone?.value || "unknown",
                  toneLabel: tone?.label || "unknown",
                  writeModel: editorOptions?.writeModel || "unknown",
                });
              }

              if (writeOpen) setWriteOpen(false);
              // if guidance is already open, then do nothing
              if (!guidanceOpen && writeMode !== WRITE_MODE_GUIDED) {
                const nonce = makeId(15);
                pluginOpenWormhole(undefined, `standard_write_${nonce}`);
              } else {
                openGuidance();
              }
            }}
            aria-label="Write"
          >
            {isWriteRunning ? <Spinner size="md" /> : <SVG src="/assets/write.svg" />}
            <div className="label-column">
              {isMyVoiceWriteModel ? (
                <>
                  <p className="small">Write</p>
                  <div className="write-state my-voice-state">{writeStateCopy}</div>
                  {showKeyDetails && <div className="write-state-details">Key details</div>}
                </>
              ) : writeMode === WRITE_MODE_AUTO ? (
                <>
                  <p>Write</p>
                  {hasMemory && <div className="write-state-details">Key details</div>}
                </>
              ) : (
                <>
                  <p className="small">Write</p>
                  <div className="write-state">
                    {writeMode === WRITE_MODE_TONE && <>{tone.label}</>}
                    {writeMode === WRITE_MODE_GUIDED && <>{writeMode.label}</>}
                  </div>
                </>
              )}
            </div>
          </button>

          <button
            type="button"
            ref={chevronRef}
            className="chevron use-component-styles"
            onClick={(e) => {
              e.stopPropagation();
              const willOpen = !writeOpen;
              setWriteOpen(willOpen);
              if (willOpen) {
                announceOpen();
              }
            }}
            aria-label="Write Options Menu"
            aria-expanded={writeOpen}
          >
            <div className="arrow">
              <SVG src="/assets/down-chevron.svg" />
            </div>
          </button>

          {writeOpen && (
            <WriteMenu
              setTone={setTone}
              openWormhole={openWormhole}
              setWriteOpen={setWriteOpen}
              setWriteMode={setWriteMode}
              writeOpen={writeOpen}
              writeMode={writeMode}
              memory={memory}
              setMemory={setMemory}
              memoryEnabled={memoryEnabled}
              setMemoryEnabled={setMemoryEnabled}
              tones={tones}
              tone={tone}
              editorOptions={editorOptions}
              wormholeOutputLength={wormholeOutputLength}
              wormholeOutputCards={wormholeOutputCards}
              creativitySliderPosition={creativitySliderPosition}
              setEditorOptions={setEditorOptions}
              openGuidance={openGuidance}
              writeModel={writeModel}
              setModalOpen={setModalOpen}
              metadata={metadata}
              setMetadata={setMetadata}
            />
          )}
        </div>
      </TryUnlimitedAndTryMuseTooltip>
    </li>
  );
});

export default WriteButton;
